import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type InputHTMLAttributes } from 'react';
import { cn, parseNumberVN } from '@/lib/utils';

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  value: number | null;
  onValueChange: (v: number | null) => void;
  /** Số chữ số thập phân tối đa khi hiển thị */
  decimals?: number;
  /** Gõ tới đâu chấm hàng nghìn tới đó (217.404), dấu thập phân là phẩy – màn điện thoại tài xế (anh Tiến 15/10/2026) */
  group?: boolean;
}

/** Định dạng chữ đang gõ: bỏ chấm cũ, chấm lại hàng nghìn; chấm gõ ở cuối khi chưa có phẩy hiểu là dấu thập phân. */
export function groupLive(input: string, decimals: number): { text: string; value: number | null } | null {
  let s = input.replace(/[\s\u00A0]/g, '');
  if (decimals > 0 && !s.includes(',') && s.endsWith('.')) s = `${s.slice(0, -1)},`;
  s = s.replace(/\./g, '');
  if (!/^\d*(,\d*)?$/.test(s)) return null;
  const [i = '', f] = s.split(',');
  const int = i.replace(/^0+(?=\d)/, '');
  const frac = f === undefined || decimals === 0 ? undefined : f.slice(0, decimals);
  const text = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + (frac === undefined ? '' : `,${frac}`);
  const value = int === '' && !frac ? null : Number(`${int || '0'}.${frac || '0'}`);
  return { text, value };
}

/**
 * Ô nhập số kiểu VN: hiển thị "1.250.000" khi không focus, khi focus hiện số thô để sửa nhanh.
 * Focus thì bôi đen toàn bộ để gõ đè.
 */
export const NumberInput = forwardRef<HTMLInputElement, Props>(({ value, onValueChange, decimals = 0, group = false, className, onFocus, onBlur, ...props }, ref) => {
  const inner = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inner.current!);
  const fmt = (v: number | null) => (v === null || v === undefined ? '' : new Intl.NumberFormat('vi-VN', { maximumFractionDigits: decimals }).format(v));
  const raw = (v: number | null) => {
    const r = v === null || v === undefined ? '' : String(v).replace('.', ',');
    return group ? (groupLive(r, decimals)?.text ?? r) : r;
  };
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(fmt(value));
  // Chữ gõ/dán không đọc được thành số: giữ nguyên giá trị cũ, tô đỏ ô, rời ô thì trả lại số cũ
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    if (!focused) setText(fmt(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, focused]);

  return (
    <input
      ref={inner}
      inputMode="decimal"
      autoComplete="off"
      aria-invalid={invalid || undefined}
      title={invalid ? 'Số không hợp lệ, giữ giá trị cũ' : props.title}
      className={cn('text-right tabular-nums', invalid && 'bg-error-soft text-error-foreground ring-1 ring-inset ring-error', className)}
      value={text}
      onFocus={(e) => {
        // Đổi giá trị + bôi đen đồng bộ ngay trong sự kiện focus: gõ nhanh ngay sau Enter không bị mất ký tự
        const r = raw(value);
        e.target.value = r;
        e.target.select();
        setFocused(true);
        setText(r);
        onFocus?.(e);
      }}
      onChange={(e) => {
        const t = e.target.value;
        if (group) {
          const g = groupLive(t, decimals);
          if (g) {
            // Giữ con trỏ sau cùng chữ số như trước khi chấm lại
            const el = e.target;
            const caret = el.selectionStart ?? t.length;
            const atEnd = caret >= t.length;
            const digitsBefore = t.slice(0, caret).replace(/[^\d,]/g, '').length;
            setText(g.text);
            setInvalid(false);
            onValueChange(g.value);
            requestAnimationFrame(() => {
              if (atEnd) return inner.current?.setSelectionRange(g.text.length, g.text.length);
              let pos = 0;
              for (let n = 0; pos < g.text.length && n < digitsBefore; pos++) if (/[\d,]/.test(g.text[pos]!)) n++;
              inner.current?.setSelectionRange(pos, pos);
            });
            return;
          }
        }
        setText(t);
        if (!t.trim()) {
          setInvalid(false);
          onValueChange(null);
          return;
        }
        const n = parseNumberVN(t);
        setInvalid(n === null);
        if (n !== null) onValueChange(n);
      }}
      onBlur={(e) => {
        setInvalid(false);
        setFocused(false);
        onBlur?.(e);
      }}
      {...props}
    />
  );
});
NumberInput.displayName = 'NumberInput';
